import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../utils/api';
import { Workspace, UserRecord } from '../../types';
import { useToast, Toast } from '../../utils/messages';
import '../../App.css';

interface WorkspaceStats {
  products: number;
  features: number;
  components: number;
  testCases: number;
  sessions: number;
  users: number;
}

interface WorkspaceWithStats extends Workspace {
  stats?: WorkspaceStats;
}

interface UserWithWorkspaces extends UserRecord {
  workspaces?: Array<{
    id: string;
    slug: string;
    name: string;
    role: string;
  }>;
}

type PortalSection = 'workspaces' | 'users';

const Portal: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState<PortalSection>('workspaces');
  const [workspaces, setWorkspaces] = useState<WorkspaceWithStats[]>([]);
  const [users, setUsers] = useState<UserWithWorkspaces[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { message, setMessage } = useToast();

  // Workspace management state
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<Workspace | null>(null);
  const [workspaceForm, setWorkspaceForm] = useState({ name: '', slug: '' });

  // User management state
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithWorkspaces | null>(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    isGoogleAccount: false,
    isPortalAdmin: false
  });
  const [selectedWorkspaceForUser, setSelectedWorkspaceForUser] = useState<string>('');
  const [userWorkspaceRole, setUserWorkspaceRole] = useState<'admin' | 'manager' | 'user'>('user');

  const fetchWorkspaces = useCallback(async () => {
    const data = await apiRequest<Workspace[]>('/api/portal/workspaces', {}, { useAuth: true, bypassWorkspace: true });
    
    // Fetch stats for each workspace
    const workspacesWithStats = await Promise.all(
      data.map(async (workspace) => {
        try {
          const stats = await apiRequest<WorkspaceStats>(
            `/api/portal/workspaces/${workspace.id}/stats`,
            {},
            { useAuth: true, bypassWorkspace: true }
          );
          return { ...workspace, stats };
        } catch (err) {
          console.error(`Error fetching stats for workspace ${workspace.id}:`, err);
          return { ...workspace, stats: undefined };
        }
      })
    );
    
    setWorkspaces(workspacesWithStats);
  }, []);

  const fetchUsers = useCallback(async () => {
    const data = await apiRequest<UserWithWorkspaces[]>('/api/portal/users', {}, { useAuth: true, bypassWorkspace: true });
    setUsers(data);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (activeSection === 'workspaces') {
        await fetchWorkspaces();
      } else {
        await fetchUsers();
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeSection, fetchWorkspaces, fetchUsers]);

  useEffect(() => {
    if (!user?.isPortalAdmin) {
      setError('Access denied. Portal admin privileges required.');
      setLoading(false);
      return;
    }

    fetchData();
  }, [user, fetchData]);

  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiRequest<Workspace>('/api/portal/workspaces', {
        method: 'POST',
        body: JSON.stringify(workspaceForm)
      }, { useAuth: true, bypassWorkspace: true });
      
      setMessage({ type: 'success', text: 'Workspace created successfully' });
      setShowCreateWorkspace(false);
      setWorkspaceForm({ name: '', slug: '' });
      await fetchWorkspaces();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to create workspace' });
    }
  };

  const handleUpdateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingWorkspace) return;
    
    try {
      await apiRequest<Workspace>(`/api/portal/workspaces/${editingWorkspace.id}`, {
        method: 'PUT',
        body: JSON.stringify(workspaceForm)
      }, { useAuth: true, bypassWorkspace: true });
      
      setMessage({ type: 'success', text: 'Workspace updated successfully' });
      setEditingWorkspace(null);
      setWorkspaceForm({ name: '', slug: '' });
      await fetchWorkspaces();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update workspace' });
    }
  };

  const handleDeleteWorkspace = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete workspace "${name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await apiRequest(`/api/portal/workspaces/${id}`, {
        method: 'DELETE'
      }, { useAuth: true, bypassWorkspace: true });
      
      setMessage({ type: 'success', text: 'Workspace deleted successfully' });
      await fetchWorkspaces();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to delete workspace' });
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newUser = await apiRequest<UserWithWorkspaces>('/api/portal/users', {
        method: 'POST',
        body: JSON.stringify(userForm)
      }, { useAuth: true, bypassWorkspace: true });
      
      // If a workspace is selected, add user to it
      if (selectedWorkspaceForUser) {
        await apiRequest(`/api/portal/workspaces/${selectedWorkspaceForUser}/users`, {
          method: 'POST',
          body: JSON.stringify({ userId: newUser.id, role: userWorkspaceRole })
        }, { useAuth: true, bypassWorkspace: true });
      }
      
      setMessage({ type: 'success', text: 'User created successfully' });
      setShowCreateUser(false);
      setUserForm({ email: '', password: '', firstName: '', lastName: '', isGoogleAccount: false, isPortalAdmin: false });
      setSelectedWorkspaceForUser('');
      await fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to create user' });
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    
    try {
      // Only send fields that should be updated, exclude password if empty
      const updateData: any = {
        email: userForm.email,
        firstName: userForm.firstName,
        lastName: userForm.lastName,
        isGoogleAccount: userForm.isGoogleAccount
      };
      
      // Only include password if it's provided and not empty
      if (userForm.password && userForm.password.trim() !== '') {
        updateData.password = userForm.password;
      }
      
      await apiRequest<UserWithWorkspaces>(`/api/portal/users/${editingUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      }, { useAuth: true, bypassWorkspace: true });
      
      setMessage({ type: 'success', text: 'User updated successfully' });
      setEditingUser(null);
      setUserForm({ email: '', password: '', firstName: '', lastName: '', isGoogleAccount: false, isPortalAdmin: false });
      await fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update user' });
    }
  };

  const handleAddUserToWorkspace = async (userId: string, workspaceId: string, role: 'admin' | 'manager' | 'user') => {
    try {
      await apiRequest(`/api/portal/workspaces/${workspaceId}/users`, {
        method: 'POST',
        body: JSON.stringify({ userId, role })
      }, { useAuth: true, bypassWorkspace: true });
      
      setMessage({ type: 'success', text: 'User added to workspace successfully' });
      await fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to add user to workspace' });
    }
  };

  const handleRemoveUserFromWorkspace = async (userId: string, workspaceId: string) => {
    if (!window.confirm('Are you sure you want to remove this user from the workspace?')) {
      return;
    }
    
    try {
      await apiRequest(`/api/portal/workspaces/${workspaceId}/users/${userId}`, {
        method: 'DELETE'
      }, { useAuth: true, bypassWorkspace: true });
      
      setMessage({ type: 'success', text: 'User removed from workspace successfully' });
      await fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to remove user from workspace' });
    }
  };

  const handleUpdateUserWorkspaceRole = async (userId: string, workspaceId: string, role: 'admin' | 'manager' | 'user') => {
    try {
      await apiRequest(`/api/portal/workspaces/${workspaceId}/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ role })
      }, { useAuth: true, bypassWorkspace: true });
      
      setMessage({ type: 'success', text: 'User role updated successfully' });
      await fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update user role' });
    }
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete user "${email}"? This action cannot be undone and will remove the user from all workspaces.`)) {
      return;
    }
    
    try {
      await apiRequest(`/api/portal/users/${userId}`, {
        method: 'DELETE'
      }, { useAuth: true, bypassWorkspace: true });
      
      setMessage({ type: 'success', text: 'User deleted successfully' });
      await fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to delete user' });
    }
  };

  const startEditWorkspace = (workspace: Workspace) => {
    setEditingWorkspace(workspace);
    setWorkspaceForm({ name: workspace.name, slug: workspace.slug });
  };

  const startEditUser = (user: UserWithWorkspaces) => {
    setEditingUser(user);
    setUserForm({
      email: user.email,
      password: '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      isGoogleAccount: !!user.isGoogleAccount,
      isPortalAdmin: false // Don't allow editing portal admin status
    });
  };

  if (!user?.isPortalAdmin) {
    return (
      <div className="form-container" style={{ textAlign: 'center', padding: '3rem' }}>
        <h1 style={{ color: 'var(--danger)', marginBottom: '1rem' }}>Access Denied</h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>You must be a portal admin to access this page.</p>
        <button className="btn btn-secondary" onClick={logout}>Logout</button>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">Portal Management</h1>
          <p className="page-subtitle">Manage workspaces and users across the platform</p>
        </div>
      </div>

      <Toast message={message} />

      {error && (
        <div style={{ 
          padding: '1rem', 
          backgroundColor: 'var(--danger-transparent)', 
          color: 'var(--danger)', 
          marginBottom: '1rem', 
          borderRadius: '4px',
          border: '1px solid var(--danger)'
        }}>
          {error}
        </div>
      )}

      <div className="flex gap-xl items-start">
        {/* Sidebar */}
        <div className="sidebar">
          <div className="flex flex-col gap-sm">
            <button
              onClick={() => setActiveSection('workspaces')}
              className={`sidebar-item ${activeSection === 'workspaces' ? 'active' : ''}`}
            >
              🏢 Workspaces
            </button>
            <button
              onClick={() => setActiveSection('users')}
              className={`sidebar-item ${activeSection === 'users' ? 'active' : ''}`}
            >
              👥 Users
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>Loading...</div>
          ) : activeSection === 'workspaces' ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h2 style={{ margin: 0 }}>Workspaces</h2>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setShowCreateWorkspace(true);
                    setEditingWorkspace(null);
                    setWorkspaceForm({ name: '', slug: '' });
                  }}
                >
                  + Create Workspace
                </button>
              </div>

              {workspaces.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  <p>No workspaces found. Create your first workspace to get started.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--grid-line)' }}>
                        <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-primary)', fontWeight: 600 }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-primary)', fontWeight: 600 }}>Slug</th>
                        <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-primary)', fontWeight: 600 }}>Statistics</th>
                        <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-primary)', fontWeight: 600 }}>Created</th>
                        <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-primary)', fontWeight: 600 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workspaces.map((workspace) => (
                        <tr key={workspace.id} style={{ borderBottom: '1px solid var(--grid-line)' }}>
                          <td style={{ padding: '1rem', color: 'var(--text-primary)' }}>{workspace.name}</td>
                          <td style={{ padding: '1rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{workspace.slug}</td>
                          <td style={{ padding: '1rem' }}>
                            {workspace.stats ? (
                              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.875rem' }}>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                  <strong style={{ color: 'var(--primary)' }}>{workspace.stats.products}</strong> Products
                                </span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                  <strong style={{ color: 'var(--primary)' }}>{workspace.stats.features}</strong> Features
                                </span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                  <strong style={{ color: 'var(--primary)' }}>{workspace.stats.components}</strong> Components
                                </span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                  <strong style={{ color: 'var(--primary)' }}>{workspace.stats.testCases}</strong> Test Cases
                                </span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                  <strong style={{ color: 'var(--primary)' }}>{workspace.stats.sessions}</strong> Sessions
                                </span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                  <strong style={{ color: 'var(--primary)' }}>{workspace.stats.users}</strong> Users
                                </span>
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>Loading...</span>
                            )}
                          </td>
                          <td style={{ padding: '1rem', color: 'var(--text-secondary)' }}>
                            {workspace.createdAt ? new Date(workspace.createdAt).toLocaleDateString() : 'N/A'}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button 
                                className="btn btn-secondary btn-sm"
                                onClick={() => startEditWorkspace(workspace)}
                              >
                                Edit
                              </button>
                              <button 
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeleteWorkspace(workspace.id, workspace.name)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Create/Edit Workspace Modal */}
              {(showCreateWorkspace || editingWorkspace) && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <div style={{
                    backgroundColor: 'var(--surface)',
                    padding: '2rem',
                    borderRadius: '4px',
                    width: '90%',
                    maxWidth: '500px',
                    border: '1px solid var(--grid-line)',
                    boxShadow: 'var(--border-glow-strong)'
                  }}>
                    <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>
                      {editingWorkspace ? 'Edit Workspace' : 'Create Workspace'}
                    </h2>
                    <form onSubmit={editingWorkspace ? handleUpdateWorkspace : handleCreateWorkspace}>
                      <div className="form-group">
                        <label>Name</label>
                        <input
                          type="text"
                          value={workspaceForm.name}
                          onChange={(e) => setWorkspaceForm({ ...workspaceForm, name: e.target.value })}
                          required
                          placeholder="Workspace Name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Slug</label>
                        <input
                          type="text"
                          value={workspaceForm.slug}
                          onChange={(e) => setWorkspaceForm({ ...workspaceForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                          required
                          placeholder="workspace-slug"
                          pattern="[a-z0-9-]+"
                        />
                      </div>
                      <div className="button-group">
                        <button type="submit" className="btn btn-primary">
                          {editingWorkspace ? 'Update' : 'Create'}
                        </button>
                        <button 
                          type="button" 
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowCreateWorkspace(false);
                            setEditingWorkspace(null);
                            setWorkspaceForm({ name: '', slug: '' });
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h2 style={{ margin: 0 }}>Users</h2>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setShowCreateUser(true);
                    setEditingUser(null);
                    setUserForm({ email: '', password: '', firstName: '', lastName: '', isGoogleAccount: false, isPortalAdmin: false });
                    setSelectedWorkspaceForUser('');
                  }}
                >
                  + Create User
                </button>
              </div>

              {users.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  <p>No users found.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', boxSizing: 'border-box' }}>
                  {users.map((userRecord) => (
                    <div 
                      key={userRecord.id}
                      style={{
                        padding: '1.5rem',
                        backgroundColor: 'var(--surface-alt)',
                        border: '1px solid var(--grid-line)',
                        borderRadius: '4px',
                        width: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <div>
                          <h3 style={{ margin: 0, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                            {userRecord.firstName || userRecord.lastName 
                              ? `${userRecord.firstName || ''} ${userRecord.lastName || ''}`.trim()
                              : 'No Name'}
                          </h3>
                          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                            {userRecord.email}
                            {userRecord.isGoogleAccount && (
                              <span style={{ marginLeft: '0.5rem', color: 'var(--primary)' }}>• Google Account</span>
                            )}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button 
                            className="btn btn-secondary btn-sm"
                            onClick={() => startEditUser(userRecord)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteUser(userRecord.id, userRecord.email)}
                          >
                            Delete User
                          </button>
                        </div>
                      </div>

                      {userRecord.workspaces && userRecord.workspaces.length > 0 && (
                        <div style={{ marginTop: '1rem', overflow: 'visible' }}>
                          <h4 style={{ margin: 0, marginBottom: '0.75rem', color: 'var(--text-primary)', fontSize: '0.875rem', fontWeight: 600 }}>
                            Workspace Memberships:
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', overflow: 'visible' }}>
                            {userRecord.workspaces.map((ws) => (
                              <div 
                                key={ws.id}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '0.75rem',
                                  backgroundColor: 'var(--void)',
                                  borderRadius: '4px',
                                  border: '1px solid var(--grid-line)',
                                  gap: '1rem',
                                  flexWrap: 'wrap',
                                  width: '100%',
                                  boxSizing: 'border-box'
                                }}
                              >
                                <div style={{ flex: '1 1 auto', minWidth: '150px', maxWidth: 'calc(100% - 250px)' }}>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{ws.name}</span>
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                      ({ws.slug})
                                    </span>
                                    <span style={{ 
                                      padding: '0.25rem 0.5rem',
                                      backgroundColor: 'var(--primary-transparent)',
                                      color: 'var(--primary)',
                                      borderRadius: '2px',
                                      fontSize: '0.75rem',
                                      textTransform: 'uppercase',
                                      fontWeight: 600
                                    }}>
                                      {ws.role}
                                    </span>
                                  </div>
                                </div>
                                <div style={{ 
                                  display: 'flex', 
                                  gap: '0.5rem', 
                                  alignItems: 'center', 
                                  flexShrink: 0,
                                  flexWrap: 'nowrap'
                                }}>
                                  <select
                                    value={ws.role}
                                    onChange={(e) => handleUpdateUserWorkspaceRole(userRecord.id, ws.id, e.target.value as 'admin' | 'manager' | 'user')}
                                    style={{
                                      padding: '0.25rem 0.5rem',
                                      fontSize: '0.875rem',
                                      backgroundColor: 'var(--surface)',
                                      border: '1px solid var(--grid-line)',
                                      borderRadius: '2px',
                                      color: 'var(--text-primary)',
                                      cursor: 'pointer',
                                      minWidth: '100px',
                                      maxWidth: '120px'
                                    }}
                                  >
                                    <option value="user">User</option>
                                    <option value="manager">Manager</option>
                                    <option value="admin">Admin</option>
                                  </select>
                                  <button 
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleRemoveUserFromWorkspace(userRecord.id, ws.id)}
                                    style={{ 
                                      padding: '0.4rem 0.75rem', 
                                      whiteSpace: 'nowrap',
                                      flexShrink: 0
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--grid-line)' }}>
                        <h4 style={{ margin: 0, marginBottom: '0.75rem', color: 'var(--text-primary)', fontSize: '0.875rem', fontWeight: 600 }}>
                          Add to Workspace:
                        </h4>
                        <AddUserToWorkspaceForm
                          userId={userRecord.id}
                          currentWorkspaces={userRecord.workspaces?.map(w => w.id) || []}
                          onAdd={handleAddUserToWorkspace}
                          onRefresh={fetchUsers}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Create/Edit User Modal */}
              {(showCreateUser || editingUser) && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <div style={{
                    backgroundColor: 'var(--surface)',
                    padding: '2rem',
                    borderRadius: '4px',
                    width: '90%',
                    maxWidth: '500px',
                    border: '1px solid var(--grid-line)',
                    boxShadow: 'var(--border-glow-strong)',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                  }}>
                    <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>
                      {editingUser ? 'Edit User' : 'Create User'}
                    </h2>
                    <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={userForm.email}
                          onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                          required
                          placeholder="user@example.com"
                          disabled={!!editingUser}
                        />
                      </div>
                      {!editingUser && (
                        <div className="form-group">
                          <label>Password</label>
                          <input
                            type="password"
                            value={userForm.password}
                            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                            required={!userForm.isGoogleAccount}
                            placeholder="Password"
                          />
                        </div>
                      )}
                      <div className="form-group">
                        <label>First Name</label>
                        <input
                          type="text"
                          value={userForm.firstName}
                          onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
                          placeholder="First Name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Last Name</label>
                        <input
                          type="text"
                          value={userForm.lastName}
                          onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
                          placeholder="Last Name"
                        />
                      </div>
                      {!editingUser && (
                        <>
                          <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={userForm.isGoogleAccount}
                                onChange={(e) => setUserForm({ ...userForm, isGoogleAccount: e.target.checked })}
                              />
                              Google Account
                            </label>
                          </div>
                          <div className="form-group">
                            <label>Add to Workspace (Optional)</label>
                            <select
                              value={selectedWorkspaceForUser}
                              onChange={(e) => {
                                setSelectedWorkspaceForUser(e.target.value);
                              }}
                            >
                              <option value="">Select a workspace...</option>
                              {workspaces.map(ws => (
                                <option key={ws.id} value={ws.id}>{ws.name}</option>
                              ))}
                            </select>
                          </div>
                          {selectedWorkspaceForUser && (
                            <div className="form-group">
                              <label>Role in Workspace</label>
                              <select
                                value={userWorkspaceRole}
                                onChange={(e) => setUserWorkspaceRole(e.target.value as 'admin' | 'manager' | 'user')}
                              >
                                <option value="user">User</option>
                                <option value="manager">Manager</option>
                                <option value="admin">Admin</option>
                              </select>
                            </div>
                          )}
                        </>
                      )}
                      <div className="button-group">
                        <button type="submit" className="btn btn-primary">
                          {editingUser ? 'Update' : 'Create'}
                        </button>
                        <button 
                          type="button" 
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowCreateUser(false);
                            setEditingUser(null);
                            setUserForm({ email: '', password: '', firstName: '', lastName: '', isGoogleAccount: false, isPortalAdmin: false });
                            setSelectedWorkspaceForUser('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface AddUserToWorkspaceFormProps {
  userId: string;
  currentWorkspaces: string[];
  onAdd: (userId: string, workspaceId: string, role: 'admin' | 'manager' | 'user') => void;
  onRefresh: () => void;
}

const AddUserToWorkspaceForm: React.FC<AddUserToWorkspaceFormProps> = ({ userId, currentWorkspaces, onAdd, onRefresh }) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<'admin' | 'manager' | 'user'>('user');
  const [loading, setLoading] = useState(false);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const data = await apiRequest<Workspace[]>('/api/portal/workspaces', {}, { useAuth: true, bypassWorkspace: true });
      // Filter out workspaces the user is already in
      const availableWorkspaces = data.filter(ws => !currentWorkspaces.includes(ws.id));
      setWorkspaces(availableWorkspaces);
    } catch (err) {
      console.error('Error fetching workspaces:', err);
    }
  }, [currentWorkspaces]);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const handleAdd = async () => {
    if (!selectedWorkspace) return;
    setLoading(true);
    try {
      await onAdd(userId, selectedWorkspace, selectedRole);
      setSelectedWorkspace('');
      await fetchWorkspaces();
      onRefresh();
    } catch (err) {
      // Error handling is done in parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Workspace:
        </label>
        <select
          value={selectedWorkspace}
          onChange={(e) => setSelectedWorkspace(e.target.value)}
          style={{ width: '100%' }}
          disabled={workspaces.length === 0}
        >
          <option value="">{workspaces.length === 0 ? 'No available workspaces' : 'Select workspace...'}</option>
          {workspaces.map(ws => (
            <option key={ws.id} value={ws.id}>{ws.name}</option>
          ))}
        </select>
      </div>
      <div style={{ flex: '0 0 150px', minWidth: '120px' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Role:
        </label>
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value as 'admin' | 'manager' | 'user')}
          style={{ width: '100%' }}
          disabled={workspaces.length === 0}
        >
          <option value="user">User</option>
          <option value="manager">Manager</option>
          <option value="admin">Admin</option>
        </select>
      </div>
      <div style={{ flex: '0 0 auto' }}>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleAdd}
          disabled={!selectedWorkspace || loading || workspaces.length === 0}
          style={{ whiteSpace: 'nowrap', marginTop: '1.5rem' }}
        >
          {loading ? 'Adding...' : 'Add to Workspace'}
        </button>
      </div>
    </div>
  );
};

export default Portal;
